import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { jwtDecode } from '../../shared/utils/jwt-decode.utils';
import { APP_API_ENDPOINTS } from '../constants/app-api.const';
import { ApiResponse } from '../models/api.model';
import { AuthStore } from '../stores/auth.store';
import { ApiService } from './api.service';

import { getDeviceInfo } from '@shared/utils/device.util';
import { APP_ROUTES } from '../constants/app-routes.const';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiService = inject(ApiService);
  private readonly store = inject(AuthStore);
  private readonly router = inject(Router);

  // --- Quản lý Refresh Token ---
  public isRefreshing = false;
  public refreshTokenSubject = new BehaviorSubject<string | null>(null);

  private toBearerAuthorization(
    value: string | null | undefined,
  ): string | null {
    if (typeof value !== 'string') return null;
    const normalized = value.trim();
    if (!normalized) return null;
    return /^Bearer\s+/i.test(normalized) ? normalized : `Bearer ${normalized}`;
  }

  /**
   * Bước 1: Khởi tạo phiên làm việc bảo mật
   */
  async initSession(deviceId: string): Promise<string> {
    const res = await this.apiService.toPromise(
      this.apiService.post<{ expires_in: number; token: string; type: string }>(
        APP_API_ENDPOINTS.AUTH.INIT_SESSION,
        {
          device_id: deviceId,
        },
      ),
    );
    return res.data!.token;
  }

  /**
   * Bước 2: Đăng nhập chính thức
   */
  async login(
    loginToken: string,
    payload: Record<string, unknown>,
  ): Promise<void> {
    const authorization = this.toBearerAuthorization(loginToken);

    if (!authorization) {
      throw new Error('Cannot login without a valid login token');
    }

    const res = await this.apiService.toPromise(
      this.apiService.post<string>(APP_API_ENDPOINTS.AUTH.LOGIN, payload, {
        headers: { Authorization: authorization },
      }),
    );

    if (res.data) {
      this.store.setToken(res.data);
    }
  }

  /**
   * API Refresh Token lấy token mới từ token cũ (trích xuất refresh_token từ claims)
   */
  refreshTokenApi(): Observable<ApiResponse<string>> {
    const currentToken = this.store.token();
    if (!currentToken) return throwError(() => new Error('No token found'));

    try {
      // ✅ Dùng Utils chung của dự án để decode JWT
      const payload = jwtDecode<{ refresh_token: string }>(currentToken);
      const refreshToken = payload.refresh_token;

      if (!refreshToken) {
        return throwError(() => new Error('No refresh token in claims'));
      }

      return this.apiService.post<string>(
        APP_API_ENDPOINTS.AUTH.REFRESH_TOKEN,
        {
          refresh_token: refreshToken,
        },
      );
    } catch {
      return throwError(() => new Error('Invalid token structure'));
    }
  }

  /**
   * Đăng xuất hoặc bị văng (Force Logout)
   */
  logout() {
    const deviceInfo = getDeviceInfo();
    this.apiService
      .post<null>(APP_API_ENDPOINTS.AUTH.LOGOUT, {
        device_id: deviceInfo.device_id,
      })
      .subscribe({
        error: (err) => {
          console.error('Logout failed:', err);
        },
      });

    this.forceLogout();
  }

  /**
   * Đăng xuất cưỡng bức (KHÔNG gọi API Logout)
   * Dùng khi token đã hết hạn hoàn toàn (refresh thất bại) —
   * gọi API logout lúc này sẽ gây loop 401.
   */
  forceLogout() {
    this.store.clearAll();
    this.router.navigate(['/' + APP_ROUTES.LOGIN]).then();
  }

  /**
   * Thay đổi mật khẩu người dùng đang đăng nhập
   */
  changePassword(payload: Record<string, string>) {
    return this.apiService.toPromise(
      this.apiService.post<boolean>(
        APP_API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
        payload,
      ),
    );
  }

  /**
   * Bước 1: Lấy mã OTP để khôi phục mật khẩu
   */
  getOtp(
    email: string,
  ): Observable<ApiResponse<{ otp: string; token: string }>> {
    return this.apiService.post<{ otp: string; token: string }>(
      APP_API_ENDPOINTS.AUTH.GET_OTP,
      { email },
    );
  }

  /**
   * Bước 2: Xác thực mã OTP
   */
  confirmOtp(payload: {
    email: string;
    otp: string;
  }): Observable<ApiResponse<boolean>> {
    return this.apiService.post<boolean>(
      APP_API_ENDPOINTS.AUTH.VERIFY_OTP,
      payload,
    );
  }

  /**
   * Bước 3: Đặt lại mật khẩu mới
   */
  forgotPassword(
    payload: Record<string, string>,
  ): Observable<ApiResponse<void>> {
    return this.apiService.post<void>(
      APP_API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      payload,
    );
  }
}
